/**
 * Provider Link Service
 *
 * Connects third-party accounts (GitHub, GitLab, Google, ...) to the signed-in
 * user. It talks to mia-flow-be's /api/account/providers/* endpoints, which are
 * backed by provider-token-service.
 */

import config from '../config.js';
import { getBeHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

class ProviderLinkService {
  /**
   * Check if a provider is linked to the user's account.
   *
   * When the backend signals that the session is temporarily unavailable (message
   * contains "Session unavailable"), the call is automatically retried up to
   * MAX_RETRIES times with an exponential back-off before returning the result.
   * This handles the case where the authtool-bff session expired mid-session
   * (e.g. GitLab disconnected during coding) without requiring a full page refresh.
   *
   * @param {string} provider - Provider alias (e.g., 'github', 'gitlab')
   * @param {object} [_opts] - Internal options (used by retry loop)
   * @returns {Promise<object>} Status object with linked property
   */
  /**
   * Refresh the authtool-bff session by calling GET /bff/session.
   * This is equivalent to what happens on a full page refresh — authtool-bff
   * re-validates the login token and creates a fresh internal session.
   * Without this, retrying a failed request hits the same dead session.
   */
  async _refreshSession() {
    try {
      const response = await fetch('/bff/session', {
        method: 'GET',
        credentials: 'include',
      });
      console.log(`🔄 [ProviderLinkService] Session refresh: HTTP ${response.status}`);
      return response.ok;
    } catch (err) {
      console.warn('⚠️ [ProviderLinkService] Session refresh failed:', err.message);
      return false;
    }
  }

  async checkLinkStatus(provider, _opts = {}) {
    const MAX_RETRIES = 3;

    const attempt = async (retryCount) => {
      try {
        const response = await getAuthorizedFetch()(
          `${config.agentUrl}/api/account/providers/link-status/${provider}`,
          {
            method: 'GET',
            headers: getBeHeaders(),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Failed to check ${provider} link status`);
        }

        const data = await response.json();

        // If the backend couldn't authenticate (session temporarily unavailable),
        // refresh the authtool-bff session first, then retry — same effect as page refresh.
        if (
          !data.linked &&
          typeof data.message === 'string' &&
          data.message.includes('Session unavailable') &&
          retryCount < MAX_RETRIES
        ) {
          console.warn(
            `⚠️ [ProviderLinkService] Session unavailable for ${provider}, ` +
            `refreshing session then retrying (attempt ${retryCount + 1}/${MAX_RETRIES})…`
          );
          await this._refreshSession();
          return attempt(retryCount + 1);
        }

        return data;
      } catch (error) {
        console.error(`Error checking ${provider} link status:`, error);
        throw error;
      }
    };

    return attempt(0);
  }

  /**
   * Ask the backend where to send the browser to connect a provider.
   *
   * @param {string} provider - Provider alias (e.g., 'github', 'gitlab')
   * @param {string} redirectUri - Where to return once linking completes
   * @returns {Promise<object>} Object with authorization_url and instructions
   */
  async initiateLinking(provider, redirectUri) {
    try {
      const baseUrl = config.agentUrl || '';
      const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      const urlString = `${cleanBase}/api/account/providers/link/${provider}`;
      const url = new URL(urlString, window.location.origin);

      // Add redirect_uri as query parameter if provided
      if (redirectUri) {
        url.searchParams.append('redirect_uri', redirectUri);
      }

      const response = await getAuthorizedFetch()(url.toString(), {
        method: 'POST',
        headers: getBeHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to initiate ${provider} linking`);
      }

      const data = await response.json();
      
      console.log('🔗 Linking - backend response:');
      console.log('   Provider:', provider);
      console.log('   Authorization URL:', data.authorization_url);

      return data;
    } catch (error) {
      console.error(`Error initiating ${provider} linking:`, error);
      throw error;
    }
  }

  /**
   * List all providers linked to the user's account
   * @returns {Promise<object>} Object with providers array and count
   */
  async listLinkedProviders() {
    try {
      const response = await getAuthorizedFetch()(
        `${config.agentUrl}/api/account/providers/linked`,
        {
          method: 'GET',
          headers: getBeHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to list linked providers');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error listing linked providers:', error);
      throw error;
    }
  }

  /**
   * Unlink a provider from the user's account
   * @param {string} provider - Provider alias (e.g., 'github', 'gitlab')
   * @returns {Promise<object>} Result object with success status
   */
  async unlinkProvider(provider) {
    try {
      const response = await getAuthorizedFetch()(
        `${config.agentUrl}/api/account/providers/unlink/${provider}`,
        {
          method: 'DELETE',
          headers: getBeHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to unlink ${provider}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error unlinking ${provider}:`, error);
      throw error;
    }
  }

  /**
   * Start a linking flow with a full-page redirect to the provider.
   * 
   * @param {string} provider - Provider alias (e.g., 'github', 'gitlab')
   * @param {string} redirectUri - Where to redirect after linking completes
   * @returns {Promise<void>}
   */
  async redirectToLinking(provider, redirectUri) {
    try {
      const result = await this.initiateLinking(provider, redirectUri);
      
      if (!result.authorization_url) {
        throw new Error('No authorization URL returned');
      }

      console.log(`🔗 Redirecting to the ${provider} linking flow...`);
      console.log(`   Return URI: ${redirectUri}`);
      console.log(`   Authorization URL: ${result.authorization_url}`);
      
      // Store provider info and return URI for post-redirect verification
      sessionStorage.setItem('pending_link_provider', provider);
      sessionStorage.setItem('pending_link_timestamp', Date.now().toString());
      sessionStorage.setItem('pending_link_return_uri', redirectUri);
      
      window.location.href = result.authorization_url;
      
    } catch (error) {
      console.error(`Error redirecting to ${provider} linking:`, error);
      throw error;
    }
  }

  /**
   * Check if we just returned from a linking flow
   * @returns {Object|null} Provider info if we just returned from linking, null otherwise
   */
  checkPostLinkingReturn() {
    // The provider redirects to the token service's callback, which finishes the
    // exchange and sends the browser back to the return URI with a clean URL.
    // So all we have to recognise here is our own pending-link marker.
    //
    // The Keycloak-era branches are gone with the flow they served: there is no
    // account-console hop any more, and no kc_action_status on the way back.
    const provider = sessionStorage.getItem('pending_link_provider');
    const timestamp = sessionStorage.getItem('pending_link_timestamp');

    if (!provider || !timestamp) {
      return null;
    }

    const clear = () => {
      sessionStorage.removeItem('pending_link_provider');
      sessionStorage.removeItem('pending_link_timestamp');
      sessionStorage.removeItem('pending_link_return_uri');
    };

    // Stale marker: the user probably abandoned the flow.
    if (Date.now() - parseInt(timestamp, 10) > 5 * 60 * 1000) {
      clear();
      return null;
    }

    clear();
    return { provider, timestamp };
  }

  /**
   * Open linking flow in popup window (DEPRECATED - use redirectToLinking instead)
   * @deprecated Use redirectToLinking instead
   * @param {string} provider - Provider alias (e.g., 'github', 'gitlab')
   * @param {Function} onComplete - Callback when linking is complete
   * @returns {Promise<void>}
   */
  async openLinkingPopup(provider, onComplete) {
    console.warn('openLinkingPopup is deprecated. Use redirectToLinking instead.');
    
    // For backward compatibility, redirect to current page
    const redirectUri = window.location.href;
    
    try {
      await this.redirectToLinking(provider, redirectUri);
      // Note: onComplete won't be called since we're doing a full redirect
    } catch (error) {
      if (onComplete) {
        onComplete(false, { error: error.message });
      }
      throw error;
    }
  }

  /**
   * Check and auto-link provider if not already linked using Client-Initiated Linking
   * @param {string} provider - Provider alias (e.g., 'github', 'gitlab')
   * @param {string} redirectUri - Where to redirect after linking
   * @returns {Promise<boolean>} True if already linked, false if linking initiated
   */
  async ensureLinked(provider, redirectUri = null) {
    try {
      // Check current status
      const status = await this.checkLinkStatus(provider);

      if (status.linked) {
        console.log(`✅ ${provider} is already linked`);
        return true;
      }

      // Not linked, show linking prompt
      const shouldLink = window.confirm(
        `To use ${provider} features, you need to link your ${provider} account.\n\n` +
        `Click OK to link your ${provider} account now. You will be redirected to complete the linking.`
      );

      if (!shouldLink) {
        console.log(`❌ User declined to link ${provider} account`);
        return false;
      }

      // Use current page as redirect if not specified
      const finalRedirectUri = redirectUri || window.location.href;

      // Initiate linking flow with redirect
      await this.redirectToLinking(provider, finalRedirectUri);

      // Note: This won't return because we're redirecting
      return false;

    } catch (error) {
      console.error(`Error ensuring ${provider} is linked:`, error);
      return false;
    }
  }
}

const providerLinkService = new ProviderLinkService();
export default providerLinkService;
